<svelte:options
  customElement={{
    tag: "oc-cinema-v1",
    /*                                            */
    extend: window.__components.extend(),
    props: {
      arrowButtonPosition: { type: "String", attribute: "arrow-button-position" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      paginationDots: { type: "Boolean", attribute: "pagination-dots" },
      itemVisibilityThreshold: { type: "Array", attribute: "item-visibility-threshold" },
      cut: { type: "String", attribute: "cut" },
      gap: { type: "String", attribute: "gap" },
      itemsPerPage: { type: "String", attribute: "items-per-page" },
      stageSpacing: { type: "String", attribute: "stage-spacing" },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";

  import { useEventDispatcher } from "@otto-ec/otto-components-utils/use/event-dispatcher";
  import { on } from "svelte/events";
  import { refireScrollEvent } from "../../../common/actions/refireScrollEvent";
  import { ArrowButtonV1 } from "../../../common/components/ArrowButton";
  import PaginationDotsV1 from "../../../common/components/PaginationDotsV1/PaginationDotsV1.svelte";

  import type { Events, Props } from "./CinemaV1.types";
  import { calcItemsPerPage } from "./math/calcItemsPerPage";
  import SkipButton from "../../../common/components/SkipButton/SkipButton.svelte";
  import DefaultStyles from "../../../common/components/DefaultStyles/DefaultStyles.svelte";
  import { calcScrollPositions } from "./math/calcScrollPositions";

  type CinemaItemHTMLElement = {
    isVisible?: boolean;
    trackingIsVisible?: boolean;
  } & HTMLElement;

  let {
    ocAriaLabel,
    arrowButtonPosition,
    resizing = "stretch-cut",
    paginationDots = false,
    itemVisibilityThreshold = undefined,
    visibleItems = [],
    cut = undefined,
    gap = undefined,
    stageSpacing: spacing = undefined,
    itemsPerPage = undefined,
  }: Props = $props();

  const Host = $host();

  const dispatch = useEventDispatcher<Events, false>(Host, false);

  let stage = $state<HTMLElement>();
  let stageScrolling = false;
  let stageScrollSource: "arrows" | "pagination-dots" | "move-method" | "swipe" = "swipe";

  let prevCurrentItemIndex: number = 0;
  let currentItemIndex = 0;
  let items = $state(Array.from(Host.children) as CinemaItemHTMLElement[]);

  let stageClientWidth: number = $state(1);
  let stageScrollLeft: number = $state(0);
  let stageScrollWidth = $state(0);

  /*                                                                                                                       */
  let update = $derived(
    /*                                        */
    /*                                                     */
    ([stage, stageClientWidth, stageScrollLeft, items] && Date.now()) as number,
  );

  /**
 *
 *
 *
 *
 *
 */
  const smoothScrollTo = (element: HTMLElement, target: number, duration = 100): void => {
    const start = element.scrollLeft;
    const change = target - start;
    /*                                     */
    const startTime = performance.now();

    const animateScroll = (currentTime: number): void => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      /*                */
      const ease =
        progress < 0.5
          ? 4 * progress * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 3) / 2;

      element.scrollLeft = start + change * ease;

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    /*                                     */
    requestAnimationFrame(animateScroll);
  };

  $effect(() => {
    if (stage && update) {
      /*                                                                     */
      requestAnimationFrame(() => {
        stageScrollWidth = stage!.scrollWidth;
      });
    }
  });
  let itemGap = $derived(
    stage ? update && parseFloat(getComputedStyle(stage).getPropertyValue("--gap")) : 0,
  );
  let stageSpacing = $derived(
    stage ? update && parseFloat(getComputedStyle(stage).getPropertyValue("--stage-spacing")) : 0,
  );
  let itemCut = $derived(
    stage ? update && parseFloat(getComputedStyle(stage).getPropertyValue("--cut")) : 0,
  );
  let userDefinedItemsPerPage = $derived(
    update &&
      parseFloat(itemsPerPage ?? getComputedStyle(Host).getPropertyValue("--items-per-page")),
  );

  let hideLeftArrow = $derived(isNaN(itemGap) || (!!update && stageScrollLeft <= itemGap));
  let hideRightArrow = $derived(
    isNaN(itemGap) ||
      (!!update && stageScrollLeft >= stageScrollWidth - stageClientWidth - itemGap),
  );

  let scrollPoints = $derived(
    stageScrollWidth ? calcScrollPositions(items, stageClientWidth, itemGap, stageSpacing) : [0],
  );

  /*                                              */
  let pageTransitionsPoints = $derived(
    scrollPoints.map((p, i) => ((scrollPoints[i - 1] ?? 0) + p) / 2),
  );

  let currentPage = $derived.by(() => {
    /*                                          */
    if (stageScrollLeft <= 0) return 0;

    /*                                                                                    */
    if (stageScrollLeft >= stageScrollWidth - stageClientWidth - itemGap)
      return scrollPoints.length - 1;

    let index = pageTransitionsPoints.findIndex((point) => point > stageScrollLeft);

    /*                                                                                                                        */
    /*                                                                                                                 */
    return index === -1 ? scrollPoints.length - 1 : index - 1;
  });

  let itemsPerPageDerived = $derived.by(() => {
    if (userDefinedItemsPerPage && !isNaN(userDefinedItemsPerPage)) {
      return userDefinedItemsPerPage;
    }
    return calcItemsPerPage(stageClientWidth, itemGap, itemCut, stageSpacing);
  });

  /*                                                                                         */
  let stretchItems = $derived(
    resizing === "stretch" || (resizing === "stretch-cut" && itemsPerPageDerived >= items.length),
  );

  export const moveTo = (
    targetSlide: number,
    options?: { source?: "arrows" | "pagination-dots"; reducedMotion?: boolean },
  ): void => {
    if (!stage) return;

    stageScrollSource = options?.source ?? "move-method";
    const targetScroll =
      scrollPoints[Math.max(targetSlide, 0)] ?? scrollPoints[scrollPoints.length - 1];

    if (options?.reducedMotion || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      stage.style.scrollBehavior = "auto";
      stage.scrollLeft = targetScroll;
    } else {
      smoothScrollTo(stage, targetScroll);
    }
  };

  let intersectingTimer = -1;

  /*                                           */
  const intersectingFinished = (): void => {
    if (currentItemIndex === prevCurrentItemIndex) return;

    stageScrolling = false;
    if (stageScrollSource !== undefined) {
      dispatch(
        "oc-stage-scroll",
        {
          isScrolling: stageScrolling,
          source: stageScrollSource,
        },
        { cancelable: false },
      );
    }

    if (itemVisibilityThreshold !== undefined && stageScrollSource !== undefined) {
      dispatch(
        "oc-item-visibility-changed",
        {
          visibleItems: {
            elements: items.filter((item) => item.trackingIsVisible),
            indices: visibleItems.map((v) => items.indexOf(v)),
          },
          scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
          stageIsScrolling: stageScrolling,
          scrollSource: stageScrollSource,
          source: stageScrollSource,
        },
        { cancelable: false },
      );
      dispatch(
        "oc-stage-scroll-completed",
        {
          visibleItems: {
            elements: items.filter((item) => item.isVisible),
            indices: visibleItems.map((v) => items.indexOf(v)),
          },
          scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
          source: stageScrollSource,
        },
        { cancelable: false },
      );
    } else {
      if (stageScrollSource !== undefined) {
        dispatch(
          "oc-item-visibility-changed",
          {
            visibleItems: {
              elements: items.filter((item) => item.isVisible),
              indices: visibleItems.map((v) => items.indexOf(v)),
            },
            scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
            stageIsScrolling: stageScrolling,
            scrollSource: stageScrollSource,
            source: stageScrollSource,
          },
          { cancelable: false },
        );
        dispatch(
          "oc-stage-scroll-completed",
          {
            visibleItems: {
              elements: items.filter((item) => item.isVisible),
              indices: visibleItems.map((v) => items.indexOf(v)),
            },
            scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
            source: stageScrollSource,
          },
          { cancelable: false },
        );
      }
    }

    prevCurrentItemIndex = currentItemIndex;
    stageScrollSource = "swipe";
  };

  const cinemaIntersecting = (): void => {
    clearTimeout(intersectingTimer);

    intersectingTimer = window.setTimeout(intersectingFinished, 500);
  };

  $effect(
    () =>
      stage &&
      on(
        stage,
        "scroll",
        () => {
          /*                                          */
          stageScrollLeft = stage.scrollLeft;
          stageScrolling = true;
          cinemaIntersecting();
        },
        { passive: true },
      ),
  );

  onMount(() =>
    on(Host, "focusin", (event) => {
      const interactiveElement = event.composedPath()[0] as HTMLElement | null;
      const item = event.target as CinemaItemHTMLElement | null;

      if (
        item instanceof HTMLElement &&
        interactiveElement?.matches(":focus-visible") &&
        !item.isVisible
      ) {
        item.scrollIntoView({ inline: "center", block: "nearest" });
      }
    }),
  );

  onMount(() => {
    /*                                                                  */
    let slideVisibilityIntersectionObserver: IntersectionObserver;
    if (itemVisibilityThreshold) {
      slideVisibilityIntersectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const target = entry.target as CinemaItemHTMLElement;
            target.trackingIsVisible = entry.isIntersecting;
          });

          visibleItems = items.filter((item) => item.trackingIsVisible);

          if (currentItemIndex !== prevCurrentItemIndex) {
            dispatch(
              "oc-stage-scroll",
              {
                isScrolling: stageScrolling,
                source: stageScrollSource,
              },
              { cancelable: false },
            );

            if (visibleItems) {
              dispatch(
                "oc-item-visibility-changed",
                {
                  visibleItems: {
                    elements: visibleItems,
                    indices: visibleItems.map((v) => items.indexOf(v)),
                  },
                  scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
                  stageIsScrolling: stageScrolling,
                  scrollSource: stageScrollSource,
                },
                { cancelable: false },
              );
            }
          }
        },
        { root: stage, threshold: itemVisibilityThreshold },
      );

      items.forEach((i) => slideVisibilityIntersectionObserver.observe(i));
    }

    let lastIntersectingItem: CinemaItemHTMLElement | null = null;

    /*                                                            */
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as CinemaItemHTMLElement;
          target.isVisible = entry.isIntersecting;
          if (entry.isIntersecting) {
            lastIntersectingItem = target;
            stageScrolling = true;
          }
        });

        /*                                                           */
        stageScrollWidth = stage!.scrollWidth;

        currentItemIndex = items.findIndex((i) => i.isVisible);
        if (currentItemIndex === -1) {
          /*                                                            */
          currentItemIndex = items.findIndex((i) => i === lastIntersectingItem);
        }

        if (!itemVisibilityThreshold) {
          visibleItems = items.filter((item) => item.isVisible);

          if (currentItemIndex !== prevCurrentItemIndex) {
            dispatch(
              "oc-stage-scroll",
              {
                isScrolling: stageScrolling,
                source: stageScrollSource,
              },
              { cancelable: false },
            );

            if (visibleItems) {
              dispatch(
                "oc-item-visibility-changed",
                {
                  visibleItems: {
                    elements: visibleItems,
                    indices: visibleItems.map((v) => items.indexOf(v)),
                  },
                  scrollDirection: currentItemIndex > prevCurrentItemIndex ? "right" : "left",
                  stageIsScrolling: stageScrolling,
                  scrollSource: stageScrollSource,
                },
                { cancelable: false },
              );
            }
          }
        }
      },
      { root: stage, threshold: [0.999, 1] },
    );

    items.forEach((i) => intersectionObserver.observe(i));

    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ removedNodes, addedNodes }) => {
        addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            intersectionObserver.observe(node);
            slideVisibilityIntersectionObserver?.observe(node);
          }
        });
        removedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            intersectionObserver.unobserve(node);
            slideVisibilityIntersectionObserver?.unobserve(node);
          }
        });
        items = Array.from(Host.children) as CinemaItemHTMLElement[];
      });
    });

    mutationObserver.observe(Host, {
      childList: true,
    });

    return () => {
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
      slideVisibilityIntersectionObserver?.disconnect();
    };
  });
</script>

<div
  class="cinema"
  style:--arrow-button-position={arrowButtonPosition}
  style:--cut={cut}
  style:--gap={gap}
  style:--items-per-page={itemsPerPage}
  style:--internal-items-per-page={itemsPerPageDerived}
  style:--stage-spacing={spacing}
>
  <SkipButton text={ocAriaLabel} offsetX="var(--gap)" />
  <section
    class={`cinema__stage cinema__stage--${resizing}`}
    class:cinema__stage--stretch={stretchItems}
    class:cinema__stage--cut={resizing === "stretch-cut" || resizing === "cut"}
    bind:this={stage}
    bind:clientWidth={stageClientWidth}
    use:refireScrollEvent={Host}
    aria-label={ocAriaLabel}
    data-oc-floating-focus-v1-target
  >
    <slot />
  </section>
  <ArrowButtonV1
    hidden={hideLeftArrow}
    direction="left"
    onclick={() => moveTo(currentPage - 1, { source: "arrows" })}
  />
  <ArrowButtonV1
    hidden={hideRightArrow}
    direction="right"
    onclick={() => moveTo(currentPage + 1, { source: "arrows" })}
  />
</div>

{#if paginationDots}
  <PaginationDotsV1 amountDots={scrollPoints.length} selectedDot={currentPage} {moveTo} />
{/if}

<DefaultStyles />

<style lang="scss" global>
  @use "@otto-ec/otto-components-utils/scss/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;
  @use "math/calcItemBase" as *;
  @use "../../floating-focus/v1/FloatingFocusV1.global";

  :host {
    /*        */
    --gap: #{tokens.$oc-component-cinema-stage-gap-x};
    --stage-spacing: #{tokens.$oc-component-cinema-stage-spacing-x};
    --cut: #{tokens.$oc-component-cinema-cut-item-visible-percentage};
    --arrow-button-position: 50%;
    --items-per-page: unset;

    display: block;
    position: relative;
  }

  .cinema {
    position: relative;

    &__stage {
      display: flex;
      gap: var(--gap);
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none; /*     */
      scroll-behavior: smooth;

      /*                                  */
      padding: 4px 0;
      margin: -4px 0;

      &::-webkit-scrollbar {
        display: none; /*                */
      }

      /*                                            */
      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }

      ::slotted(*) {
        flex: 0 0 auto;
        max-width: 100%;
        min-width: 0;
      }

      &--cut {
        ::slotted(*) {
          flex: 0 0
            calcItemBase(
              var(--items-per-page, var(--internal-items-per-page)),
              var(--gap),
              var(--cut),
              var(--stage-spacing)
            );
        }
      }

      &--stretch {
        ::slotted(*) {
          flex: 1 0 auto;
        }
      }
    }
  }

  /*                                               */
  .cinema__stage--stretch.cinema__stage--cut {
    ::slotted(*) {
      flex: 1 0 0;
    }
  }
</style>
